import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function CompleteRegistrationPage() {
  const navigate = useNavigate();
  const { updateToken } = useAuth();
  const [companyData, setCompanyData] = useState<any>(null);
  const [packageData, setPackageData] = useState<any>(null);

  const [formData, setFormData] = useState({
    adminEmail: "",
    adminPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Load stored registration data
    const company = sessionStorage.getItem("companyRegistration");
    const pkg = sessionStorage.getItem("selectedPackage");

    if (!company || !pkg) {
      navigate("/register/company");
      return;
    }

    setCompanyData(JSON.parse(company));
    setPackageData(JSON.parse(pkg));

    // Pre-fill admin email from company contact
    const companyInfo = JSON.parse(company);
    setFormData(prev => ({ ...prev, adminEmail: companyInfo.contactEmail || "" }));
  }, [navigate]);

  function handleChange(field: string, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (formData.adminPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password strength
    if (formData.adminPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSubmitting(true);

    try {
      // Create organization and admin user
      const response = await apiFetch("/api/register/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company: companyData,
          package: packageData,
          admin: {
            email: formData.adminEmail,
            password: formData.adminPassword,
            name: companyData.contactName,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Registration failed");
      }

      // Clear session storage
      sessionStorage.removeItem("companyRegistration");
      sessionStorage.removeItem("selectedPackage");

      if (data.token) {
        updateToken(data.token);
        navigate("/app", { replace: true });
        return;
      }
      navigate("/login?registered=true");
    } catch (err: any) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!companyData || !packageData) {
    return null;
  }

  const totalSeats = packageData.adminSeats + packageData.operatorSeats;
  const monthlyPrice = totalSeats * packageData.pricePerSeat;

  return (
    <div className="min-h-screen bg-background px-4 py-16">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-foreground hover:opacity-90">
            <img src="/logo.png" alt="CompostDeveloper" className="h-10 w-10 rounded" />
            <span className="text-xl font-bold">
              Compost<span className="text-forest-500">Developer</span>
            </span>
          </Link>
          <h1 className="mt-6 text-3xl font-semibold text-foreground">Complete Registration</h1>
          <p className="mt-2 text-muted-foreground">
            Review your selections and create your admin account
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Summary Column */}
          <div className="space-y-6">
            {/* Company Summary */}
            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Company Information</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Company Name</div>
                  <div className="text-foreground font-medium">{companyData.companyName}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Location</div>
                  <div className="text-foreground font-medium">
                    {companyData.facilityLocation || "Not specified"}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Facility Size</div>
                  <div className="text-foreground font-medium capitalize">
                    {companyData.facilitySize.replace(/_/g, " ")}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Primary Contact</div>
                  <div className="text-foreground font-medium">{companyData.contactName}</div>
                  <div className="text-foreground">{companyData.contactEmail}</div>
                </div>
              </div>
            </div>

            {/* Package Summary */}
            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Selected Package</h2>
              <div className="space-y-3">
                <div>
                  <div className="text-xl font-bold text-foreground">{packageData.displayName}</div>
                  <div className="text-sm text-muted-foreground">{packageData.description}</div>
                </div>

                <div className="pt-3 border-t border-border">
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-3xl font-bold text-foreground">${monthlyPrice}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-forest-600" />
                      <span className="text-foreground">
                        {packageData.adminSeats} Admin seat{packageData.adminSeats > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-forest-600" />
                      <span className="text-foreground">
                        {packageData.operatorSeats} Operator seat{packageData.operatorSeats > 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Creation Form */}
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Create Admin Account</h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Admin Email
                </label>
                <input
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => handleChange("adminEmail", e.target.value)}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                  required
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  This will be your login email
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.adminPassword}
                  onChange={(e) => handleChange("adminPassword", e.target.value)}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                  required
                  minLength={8}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Minimum 8 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  className="w-full px-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 text-sm"
                  required
                  minLength={8}
                />
              </div>

              <div className="pt-4 space-y-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 bg-forest-600 hover:bg-forest-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Creating Account..." : "Complete Registration"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/register/package")}
                  className="w-full px-6 py-3 border border-border rounded-lg text-foreground hover:bg-secondary transition-colors font-medium"
                >
                  Back to Packages
                </button>
              </div>

              <p className="text-xs text-muted-foreground text-center pt-4">
                By registering, you agree to our Terms of Service and Privacy Policy
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
